/* eslint-disable react/no-array-index-key */
import LandingPage from '@website/components/LandingPages/LandingPage';
import { LandingPageContent } from '@website/components/LandingPages/LandingPageContentType';
import { algoliaSearch } from '@website/components/LandingPages/algoliaSearch';
import {
  filterByToAlgoliaFieldMap,
  landingPageForFilterBy,
  landingPageQuery,
} from '@website/components/LandingPages/queries';
import DefaultLayout from '@website/components/layouts/DefaultLayout';
import { TripSearchIndex } from '@website/types/TripSearchIndex';
import { GetStaticPathsResult, GetStaticPropsContext } from 'next';
import { PropsWithChildren } from 'react';
import { contentFulClient } from '../blog/[postSlug]';

export interface AllLandingPagesQueryResponse {
  landingPagesCollection: {
    items: {
      slug: string;
    }[];
  };
}

type LandingPagesUrlsParams = {
  slug: string;
};

/**
 * Get the paths to pre-render the top 100 blogs
 */
export async function getStaticPaths(): Promise<
  GetStaticPathsResult<LandingPagesUrlsParams>
> {
  const { data } = await contentFulClient.query({
    query: landingPageForFilterBy,
    variables: {
      filterBy: 'Tag',
    },
  });

  const { landingPagesCollection } = data as AllLandingPagesQueryResponse;

  const paths = landingPagesCollection.items.map((page) => ({
    params: {
      slug: page.slug,
    },
  }));

  return {
    paths,
    // Enable fallback so that new blog posts will be generated on first load, rather than waiting for a new deploy
    fallback: true,
  };
}

/**
 * Get Landing Page
 */
export async function getStaticProps({
  params: { slug },
}: GetStaticPropsContext<LandingPagesUrlsParams>) {
  const res = await contentFulClient.query({
    query: landingPageQuery,
    variables: {
      slug,
    },
    fetchPolicy: 'no-cache',
  });

  const page = (await res.data.landingPagesCollection
    .items[0]) as LandingPageProps['page'];
  const filterBy = filterByToAlgoliaFieldMap.get(page?.filterBy);

  const resultsState = await algoliaSearch(filterBy, page.filterValue);

  return {
    props: {
      page,
      trips: resultsState.results[0].hits,
      totalPages: resultsState.results[0].nbPages,
      filterByValue: page.filterValue,
    },
    // revalidate every 1 hour
    revalidate: 3600,
  };
}

type LandingPageProps = {
  trips: TripSearchIndex[];
  page: LandingPageContent;
  totalPages: number;
  filterByValue: string;
};

export default function CustomTagsPage({
  page,
  trips,
  filterByValue,
  totalPages,
}: LandingPageProps) {
  return (
    <LandingPage
      page={page}
      trips={trips}
      totalPages={totalPages}
      filterBy="tags"
      filterByValue={filterByValue}
    />
  );
}

CustomTagsPage.Layout = function Layout({ children }: PropsWithChildren<{}>) {
  return <DefaultLayout className="">{children}</DefaultLayout>;
};
